// PINNED REPORTS (owner, 2026-11-04 — D-470): a person pins up to five of the
// Reporting rail's reports, which then also show in a Pinned section at the top
// of the rail, in the order they were pinned.
//
// Kept on the server, per person (`GET` / `PUT /me/report-pins`, `docs/api.md`
// §16b), so pins follow the person to any browser. Read once when Reporting
// opens; each change saves the whole list. A save that fails puts the previous
// pins back and says so. The server holds the same limit and refuses an id that
// names no report.
import { useCallback, useEffect, useState } from 'react'
import { MAX_REPORT_PINS } from '@ap/shared'
import { errorMessage } from '../api/errors'
import { loadReportPins, saveReportPins } from './data'

/** The most reports one person may pin. */
export const MAX_PINS = MAX_REPORT_PINS

/** The rail's search, shared by the Pinned section and the others: a report's title or its section's name. */
export function railMatches(entry: { title: string; group: string }, query: string, section = entry.group): boolean {
  const q = query.trim().toLowerCase()
  return q === '' || entry.title.toLowerCase().includes(q) || section.toLowerCase().includes(q)
}

/** The pins as the rail shows them: oldest first, each a report the rail has, once, at most five. */
function cleanPins(ids: readonly unknown[], knownIds: ReadonlySet<string>): string[] {
  const out: string[] = []
  for (const id of ids) {
    if (typeof id === 'string' && knownIds.has(id) && !out.includes(id)) out.push(id)
  }
  return out.slice(0, MAX_PINS)
}

/** Pins a report at the end, or unpins it. With five pinned, pinning another returns the same list. */
function togglePin(pins: readonly string[], id: string): readonly string[] {
  if (pins.includes(id)) return pins.filter((p) => p !== id)
  if (pins.length >= MAX_PINS) return pins
  return [...pins, id]
}

/** The sentence a failed save shows, beside the reason the server gave. */
function pinSaveFailure(e: unknown): string {
  return `Your pinned reports were not saved, so they are back as they were. ${errorMessage(e)}`
}

/**
 * The pins between the screen and the server, without React: what is shown,
 * what the server last confirmed, and the saves, one at a time and in order so
 * an older answer never overwrites a newer list. A failed save shows the
 * confirmed list again and reports why.
 */
export function createPinStore(options: {
  knownIds: ReadonlySet<string>
  save: (ids: readonly string[]) => Promise<string[]>
  onChange: (pins: readonly string[]) => void
}) {
  let shown: readonly string[] = []
  let confirmed: readonly string[] = []
  let queue: Promise<void> = Promise.resolve()
  const show = (pins: readonly string[]) => {
    shown = pins
    options.onChange(pins)
  }
  return {
    /** The list the server answered with when Reporting opened. */
    loaded(ids: readonly unknown[]): void {
      confirmed = cleanPins(ids, options.knownIds)
      show(confirmed)
    },
    /** Pins or unpins `id` and saves the list; a failed save is told to `onError`. False when nothing changed. */
    toggle(id: string, onError: (message: string) => void): boolean {
      const next = togglePin(shown, id)
      if (next === shown) return false
      show(next)
      queue = queue.then(async () => {
        try {
          confirmed = cleanPins(await options.save(next), options.knownIds)
          if (shown === next) show(confirmed)
        } catch (e) {
          show(confirmed)
          onError(pinSaveFailure(e))
        }
      })
      return true
    },
    /** Resolves once every save asked for so far has answered. */
    settled(): Promise<void> {
      return queue
    },
  }
}

/**
 * The signed-in person's pins. `ready` is false until the first read answers,
 * and stays false when it fails, so nothing is saved over pins that were never
 * read. `toggle` answers false when nothing changed (five already pinned).
 */
export function useReportPins(knownIds: ReadonlySet<string>, onError: (message: string) => void) {
  const [pins, setPins] = useState<readonly string[]>([])
  const [ready, setReady] = useState(false)
  const [store] = useState(() => createPinStore({ knownIds, save: saveReportPins, onChange: setPins }))

  useEffect(() => {
    let cancelled = false
    loadReportPins().then(
      (ids) => {
        if (cancelled) return
        store.loaded(ids)
        setReady(true)
      },
      (e: unknown) => {
        if (!cancelled) onError(`Your pinned reports could not be loaded. ${errorMessage(e)}`)
      },
    )
    return () => {
      cancelled = true
    }
  }, [store, onError])

  const toggle = useCallback((id: string): boolean => ready && store.toggle(id, onError), [ready, store, onError])

  return { pins, ready, toggle }
}
